import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  Calendar,
  User,
  MapPin,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Image as ImageIcon,
  Share2,
  Copy,
  Printer,
  Edit3,
  X,
  Trash2,
  AlertTriangle,
  Clock,
} from "lucide-react"
import { useModalOpen } from "../lib/useModalOpen"
import { useGlassTokens } from "../lib/useAppBackground"
import type { GlassTokens } from "../lib/appBackground"
import { useSwipe } from "../lib/useSwipe"
/** Glass pill classes for status in glitchOS modal. */
function getReportStatusGlassClasses(status: string, defaultBorder: string): string {
  switch (status) {
    case "Completed":
      return "bg-emerald-500/20 text-emerald-400 border-emerald-400/25"
    case "In Progress":
      return "bg-blue-500/20 text-blue-400 border-blue-400/25"
    case "Scheduled":
      return "bg-amber-500/20 text-amber-400 border-amber-400/25"
    default:
      return `bg-white/10 text-white/60 ${defaultBorder}`
  }
}
import { getReportDisplayTitle } from "../lib/relativeDate"
import { ServiceTypePill } from "../lib/serviceTypeDisplay"
import { DEFAULT_CHECKLIST } from "../lib/checklistTemplate"
import { ReportEditForm } from "./ReportEditForm"
import type { CleanReport } from "../lib/supabase"
import type { GeminiSupplyAnalysis } from "../lib/geminiSupplyAnalysisSchema"
import { useToast } from "./Toast"
import { RichTextRenderer } from "./RichTextRenderer"

// ── Types ────────────────────────────────────────────────────────────────────

export type ReportForDetailModal = {
  id?: string
  title: string
  status?: string
  service_type?: string
  activity_date?: string
  clean_date?: string
  checklist_notes?: string
  photos?: string[]
  checklist?: Record<string, { tasks: { task: string; completed: boolean }[] }>
  property?: { name?: string }
  operator_name?: string
  operator_name_legacy?: string
  time_entry_id?: string | null
  duration_minutes?: number | null
  // Supply Synapse / Intelligence
  supply_snap_photo?: string | null
  supply_snap_estimate?: number | null
  supply_snap_room?: string | null
  site_sense_audio_url?: string | null
  gemini_analysis?: GeminiSupplyAnalysis | null
  analysis_status?: "pending" | "completed" | "failed" | null
}

function toReportForDetailModal(
  r: CleanReport,
  context?: { propertyName?: string }
): ReportForDetailModal {
  return {
    id: r.id,
    title: r.title,
    status: r.status ?? undefined,
    service_type: r.service_type ?? undefined,
    activity_date: r.activity_date,
    checklist_notes: r.checklist_notes ?? undefined,
    photos: r.photos ?? [],
    checklist: r.checklist ?? undefined,
    property: context?.propertyName ? { name: context.propertyName } : undefined,
    operator_name: r.operator_name ?? undefined,
    time_entry_id: r.time_entry_id,
    supply_snap_photo: r.supply_snap_photo,
    supply_snap_estimate: r.supply_snap_estimate,
    supply_snap_room: r.supply_snap_room,
    site_sense_audio_url: r.site_sense_audio_url,
    gemini_analysis: r.gemini_analysis,
    analysis_status: r.analysis_status,
  }
}

function getActivityDate(report: ReportForDetailModal): string {
  return report.activity_date ?? report.clean_date ?? ""
}

function getOperatorName(report: ReportForDetailModal): string {
  return report.operator_name ?? report.operator_name_legacy ?? ""
}

// ── Photo viewer (single shared component) ───────────────────────────────────

function PhotoViewer({
  photos,
  current,
  onChange,
  onExpand,
  className = "",
  glass,
}: {
  photos: string[]
  current: number
  onChange: (idx: number) => void
  onExpand?: () => void
  className?: string
  glass: GlassTokens
}) {
  const [loaded, setLoaded] = useState<Set<string>>(new Set())
  const preloadRef = useRef<Map<string, HTMLImageElement>>(new Map())
  const swipe = useSwipe({
    onSwipeLeft: () => photos.length > 1 && onChange((current + 1) % photos.length),
    onSwipeRight: () => photos.length > 1 && onChange(current === 0 ? photos.length - 1 : current - 1),
  })

  useEffect(() => {
    const cache = preloadRef.current
    photos.forEach((url) => {
      if (cache.has(url)) return
      const img = new Image()
      img.onload = () => setLoaded((prev) => { const n = new Set(prev); n.add(url); return n })
      img.src = url
      cache.set(url, img)
    })
  }, [photos])

  const isLoaded = photos.length === 0 || loaded.has(photos[current])

  if (photos.length === 0) {
    return (
      <div className={`aspect-video flex flex-col items-center justify-center bg-white/[0.04] border ${glass.border} text-white/40 ${className}`}>
        <ImageIcon className="w-10 h-10 mb-2 opacity-40" />
        <p className="font-mono text-[13px] text-white/40">No photos</p>
      </div>
    )
  }

  return (
    <div className={`relative ${glass.cardBg} overflow-hidden border ${glass.border} ${className}`} {...swipe}>
      {/* Image */}
      {!isLoaded && <div className="absolute inset-0 bg-white/[0.04] animate-pulse" />}
      <button
        type="button"
        onClick={onExpand}
        className={`block w-full h-full cursor-zoom-in focus:outline-none ${onExpand ? "" : "pointer-events-none"}`}
        aria-label="Open gallery"
        tabIndex={onExpand ? 0 : -1}
      >
        <img
          src={photos[current]}
          alt={`Photo ${current + 1} of ${photos.length}`}
          className={`w-full h-full object-cover select-none transition-opacity duration-200 ${isLoaded ? "opacity-100" : "opacity-0"}`}
          draggable={false}
          onLoad={() => setLoaded((prev) => { const n = new Set(prev); n.add(photos[current]); return n })}
        />
      </button>

      {/* Nav arrows — always visible on touch, not hover-gated */}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(current === 0 ? photos.length - 1 : current - 1) }}
            className={`absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 bg-white/[0.08] border ${glass.border} hover:bg-white/[0.12] text-white rounded-full flex items-center justify-center transition-colors touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange((current + 1) % photos.length) }}
            className={`absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 bg-white/[0.08] border ${glass.border} hover:bg-white/[0.12] text-white rounded-full flex items-center justify-center transition-colors touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
            aria-label="Next photo"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          {/* Dots */}
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
            {photos.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === current ? "bg-white scale-125" : "bg-white/50"}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Count badge */}
      {photos.length > 1 && (
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-lg ${glass.cardBg} backdrop-blur-sm border ${glass.border} text-white text-[10px] font-mono font-bold uppercase tracking-wider pointer-events-none`}>
          {current + 1} / {photos.length}
        </div>
      )}
    </div>
  )
}

// ── Intelligence (Supply Synapse / Gemini) ────────────────────────────────────

function hasIntelligenceData(r: ReportForDetailModal): boolean {
  if (r.supply_snap_photo || r.supply_snap_estimate != null || r.supply_snap_room || r.site_sense_audio_url || r.analysis_status) return true
  const g = r.gemini_analysis
  if (!g) return false
  return !!(g.inventory_level || g.verification || (g.supplies_identified?.length) || g.usage_prediction || g.facility_pulse?.traffic || (g.facility_pulse?.maintenance_issues?.length) || g.operator_sentiment || g.anomaly_flag || g.reorder_suggestion)
}

function IntelligenceSection({ report, onPhotoClick, glass }: { report: ReportForDetailModal; onPhotoClick?: (idx: number) => void; glass: GlassTokens }) {
  const g = report.gemini_analysis
  const hasSupply = report.supply_snap_photo != null || report.supply_snap_estimate != null || report.supply_snap_room != null
  const hasAnalysis = g && (g.inventory_level || g.verification || (g.supplies_identified?.length) || g.usage_prediction || g.facility_pulse || g.operator_sentiment || g.anomaly_flag || g.reorder_suggestion)

  return (
    <div className="rounded-2xl border border-teal-400/20 bg-teal-950/30 overflow-hidden">
      <div className={`flex items-center justify-between px-3 py-2 border-b ${glass.borderLight}`}>
        <span className="text-[10px] font-mono font-bold text-teal-400/90 uppercase tracking-widest">Intelligence</span>
        {report.analysis_status && (
          <span
            className={`text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg ${
              report.analysis_status === "completed"
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-400/25"
                : report.analysis_status === "failed"
                  ? "bg-red-500/20 text-red-400 border border-red-400/25"
                  : "bg-amber-500/20 text-amber-400 border border-amber-400/25 animate-pulse"
            }`}
          >
            {report.analysis_status}
          </span>
        )}
      </div>
      <div className="p-3 space-y-3">
        {hasSupply && (
          <div className="space-y-2">
            {report.supply_snap_photo && (
              <button
                type="button"
                onClick={() => onPhotoClick?.(0)}
                className={`block w-full max-w-[120px] aspect-video rounded-lg overflow-hidden border ${glass.border} hover:border-teal-400/30 transition-colors`}
              >
                <img src={report.supply_snap_photo} alt="Supply snap" className="w-full h-full object-cover" />
              </button>
            )}
            {report.supply_snap_estimate != null && (
              <div>
                <p className="text-[9px] font-mono text-white/40 uppercase tracking-wider mb-1.5">Supply Level</p>
                <div className="flex items-baseline gap-2 mb-1.5">
                  <span
                    className="text-2xl font-mono font-black tabular-nums"
                    style={{ color: report.supply_snap_estimate <= 33 ? "rgb(248 113 113)" : report.supply_snap_estimate <= 66 ? "rgb(251 191 36)" : "rgb(52 211 153)" }}
                  >
                    {report.supply_snap_estimate}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${report.supply_snap_estimate}%`,
                      background: report.supply_snap_estimate <= 33 ? "rgb(248 113 113)" : report.supply_snap_estimate <= 66 ? "rgb(251 191 36)" : "rgb(52 211 153)",
                    }}
                  />
                </div>
              </div>
            )}
            {report.supply_snap_room && (
              <p className="text-[10px] font-mono text-white/60">Room: {report.supply_snap_room}</p>
            )}
          </div>
        )}
        {hasAnalysis && g && (
          <div className={`space-y-1.5 text-[10px] font-mono border-t ${glass.borderLight} pt-2`}>
            {g.inventory_level && <p className="text-white/70">{g.inventory_level}</p>}
            {g.verification && <p className="text-teal-400/90">Verification: {g.verification}</p>}
            {g.supplies_identified?.length ? <p className="text-white/60">Supplies: {g.supplies_identified.join(", ")}</p> : null}
            {g.usage_prediction && <p className="text-amber-400/90">{g.usage_prediction}</p>}
            {g.facility_pulse?.traffic && <p className="text-white/50">Traffic: {g.facility_pulse.traffic}</p>}
            {g.facility_pulse?.maintenance_issues?.length ? <p className="text-red-400/80">Issues: {g.facility_pulse.maintenance_issues.join("; ")}</p> : null}
            {g.operator_sentiment && <p className="text-white/50">Sentiment: {g.operator_sentiment}</p>}
            {g.anomaly_flag && <p className="text-amber-400/90">Anomaly flagged</p>}
            {g.reorder_suggestion && <p className="text-teal-400/90">{g.reorder_suggestion}</p>}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Checklist ────────────────────────────────────────────────────────────────

function ServiceChecklist({ checklist, glass }: { checklist: any; glass: GlassTokens }) {
  const categories = Object.keys(checklist ?? {})
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(
    () => categories.length > 0 ? { [categories[0]]: true } : {}
  )
  if (!checklist || categories.length === 0) return null

  const toggleCategory = (cat: string) =>
    setOpenCategories((prev) => ({ ...prev, [cat]: !prev[cat] }))
  const setAllOpen = (open: boolean) =>
    setOpenCategories(categories.reduce<Record<string, boolean>>((a, c) => ({ ...a, [c]: open }), {}))

  const tasksFor = (data: any): { label: string; completed: boolean }[] => {
    if (!data?.tasks) return []
    return data.tasks.map((t: any) =>
      typeof t === "string"
        ? { label: t, completed: true }
        : { label: t.task ?? t.label ?? "", completed: t.completed !== false }
    )
  }

  return (
    <div className={`rounded-2xl border ${glass.border} ${glass.cardBg} overflow-hidden`}>
      <div className={`flex items-center justify-end gap-1 px-3 py-2 border-b ${glass.borderLight} bg-white/[0.02]`}>
        <button type="button" onClick={() => setAllOpen(true)} className="text-[10px] font-mono text-white/50 hover:text-white/80 px-2 py-1 rounded transition-colors">
          Expand all
        </button>
        <span className="text-white/30 font-mono text-[13px]">|</span>
        <button type="button" onClick={() => setAllOpen(false)} className="text-[10px] font-mono text-white/50 hover:text-white/80 px-2 py-1 rounded transition-colors">
          Collapse all
        </button>
      </div>
      {Object.entries(checklist).map(([category, data]: [string, any]) => {
        const tasks = tasksFor(data)
        const completedCount = tasks.filter((t) => t.completed).length
        const total = tasks.length
        const isOpen = openCategories[category] ?? false
        const iconColor =
          total === 0 || completedCount === 0
            ? "text-white/20"
            : completedCount < total
              ? "text-amber-400"
              : "text-emerald-400"
        return (
          <div key={category} className={`border-b ${glass.borderLight} last:border-b-0`}>
            <button
              type="button"
              onClick={() => toggleCategory(category)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.04] transition-colors text-left"
            >
              <span className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-widest text-white/80">
                <CheckCircle className={`w-4 h-4 shrink-0 ${iconColor}`} />
                {category}
                {tasks.length > 0 && (
                  <span className="text-[10px] font-normal text-white/40 normal-case tracking-normal">
                    ({completedCount}/{total})
                  </span>
                )}
              </span>
              <ChevronDown className={`w-4 h-4 text-white/40 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && tasks.length > 0 && (
              <div className="px-4 pb-3 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {tasks.map((task, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 py-2 px-3 rounded-lg text-[11px] font-mono ${
                      task.completed
                        ? `bg-white/[0.04] border ${glass.border} text-white/80`
                        : "bg-transparent border border-white/[0.04] text-white/40"
                    }`}
                  >
                    <span className={`shrink-0 w-3.5 h-3.5 rounded-full ${task.completed ? "bg-emerald-500/80" : "border-2 border-white/20"}`} />
                    <span className="font-bold">{task.label || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Full-screen gallery overlay ───────────────────────────────────────────────

function GalleryOverlay({
  photos,
  current,
  onChange,
  onClose,
  glass,
}: {
  photos: string[]
  current: number
  onChange: (idx: number) => void
  onClose: () => void
  glass: GlassTokens
}) {
  return createPortal(
    <div className="fixed inset-0 z-[110] flex flex-col items-center justify-center bg-black/80 backdrop-blur-md">
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors z-10 touch-manipulation"
        aria-label="Close gallery"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Image */}
      <PhotoViewer
        photos={photos}
        current={current}
        onChange={onChange}
        glass={glass}
        className={`w-full max-w-5xl aspect-auto border ${glass.border}`}
      />

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div className="flex gap-2 mt-4 justify-center flex-wrap px-4">
          {photos.map((url, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onChange(i)}
              className={`w-12 h-12 rounded-lg overflow-hidden border transition-all touch-manipulation ${i === current ? `ring-2 ring-white/60 border ${glass.border}` : `border ${glass.border} opacity-50 hover:opacity-80`}`}
              aria-label={`Photo ${i + 1}`}
            >
              <img src={url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <p className="text-white/50 text-[10px] font-mono mt-3">{current + 1} / {photos.length}</p>
    </div>,
    document.body
  )
}

// ── Main modal ───────────────────────────────────────────────────────────────

export function ReportDetailModal({
  report,
  onClose,
  onEdit,
  onReportUpdated,
  isAdmin,
  onDeleteReport,
}: {
  report: ReportForDetailModal
  onClose: () => void
  onEdit?: (reportId: string) => void
  onReportUpdated?: (report: ReportForDetailModal) => void
  isAdmin?: boolean
  onDeleteReport?: (reportId: string) => void | Promise<void>
}) {
  const glass = useGlassTokens()
  useModalOpen(true)
  const { toast } = useToast()
  const [displayReport, setDisplayReport] = useState<ReportForDetailModal>(report)
  const [isEditing, setIsEditing] = useState(false)
  const [currentPhoto, setCurrentPhoto] = useState(0)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [copyFeedback, setCopyFeedback] = useState(false)
  // Inline delete confirmation — avoids window.confirm()
  const [deleteConfirming, setDeleteConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const deleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => { setDisplayReport(report) }, [report])

  // Keyboard: Esc, ←/→
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (galleryOpen) {
        if (e.key === "Escape") setGalleryOpen(false)
        if (e.key === "ArrowLeft") setCurrentPhoto((p) => (p === 0 ? photos.length - 1 : p - 1))
        if (e.key === "ArrowRight") setCurrentPhoto((p) => (p + 1) % photos.length)
        return
      }
      if (isEditing && e.key === "Escape") { setIsEditing(false); return }
      if (shareOpen && e.key === "Escape") { setShareOpen(false); return }
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose, shareOpen, isEditing, galleryOpen]) // eslint-disable-line

  // Cancel delete confirmation after 4s
  useEffect(() => {
    if (deleteConfirming) {
      deleteTimerRef.current = setTimeout(() => setDeleteConfirming(false), 4000)
    }
    return () => { if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current) }
  }, [deleteConfirming])

  const photos =
    displayReport.supply_snap_photo
      ? [displayReport.supply_snap_photo, ...(displayReport.photos || [])]
      : (displayReport.photos || [])
  const displayTitle = getReportDisplayTitle(displayReport)
  const showIntelligence = hasIntelligenceData(displayReport)
  const activityDate = getActivityDate(displayReport)
  const operatorName = getOperatorName(displayReport)

  const reportUrl =
    typeof window !== "undefined" && displayReport.id
      ? `${window.location.origin}${window.location.pathname}?report=${displayReport.id}`
      : typeof window !== "undefined" ? window.location.href : ""

  const reportSummary = [
    displayTitle,
    displayReport.property?.name,
    activityDate
      ? new Date(activityDate).toLocaleDateString(undefined, { dateStyle: "long" })
      : "",
    displayReport.service_type ? `Protocol: ${displayReport.service_type}` : "",
    operatorName ? `Specialist: ${operatorName}` : "",
  ].filter(Boolean).join("\n")

  const checklistData =
    displayReport.checklist && Object.keys(displayReport.checklist).length > 0
      ? displayReport.checklist
      : Object.fromEntries(
          Object.entries(DEFAULT_CHECKLIST).map(([cat, data]) => [
            cat,
            { tasks: data.tasks.map((t) => ({ task: t.task, completed: false })) },
          ])
        )

  const handleSaveFromEdit = (updated: CleanReport, context?: { propertyName?: string }) => {
    const next = toReportForDetailModal(updated, context)
    setDisplayReport(next)
    setIsEditing(false)
    onReportUpdated?.(next)
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${reportSummary}\n\n${reportUrl}`)
      setCopyFeedback(true)
      setTimeout(() => setCopyFeedback(false), 2000)
      setShareOpen(false)
    } catch { /* ignore */ }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: displayTitle, text: reportSummary, url: reportUrl })
        setShareOpen(false)
      } catch (err) {
        if ((err as Error).name !== "AbortError") handleCopy()
      }
    } else {
      handleCopy()
    }
  }

  const handlePrint = () => {
    setShareOpen(false)
    const printChecklist =
      displayReport.checklist && Object.keys(displayReport.checklist).length > 0
        ? displayReport.checklist
        : Object.fromEntries(
            Object.entries(DEFAULT_CHECKLIST).map(([cat, data]) => [
              cat,
              { tasks: data.tasks.map((t) => ({ task: t.task, completed: false })) },
            ])
          )
    const checklistHtml = Object.keys(printChecklist).length > 0
      ? `<div class="checklist"><strong>Checklist</strong>${Object.entries(printChecklist).map(
          ([cat, data]: [string, any]) =>
            `<div class="category">${cat}</div>${(data.tasks || []).map((t: any) => {
              const label = typeof t === "string" ? t : (t.task || t.label || "")
              const done = typeof t === "object" && t.completed === true
              return `<div class="task ${done ? "task-done" : "task-pending"}">${label}</div>`
            }).join("")}`
        ).join("")}</div>`
      : ""
    const photosHtml = photos.length > 0
      ? `<div class="photo-gallery">${photos.map((url) => `<div class="photo-wrap"><img src="${url}" alt="Report photo" /></div>`).join("")}</div>`
      : ""
    const win = window.open("", "_blank")
    if (!win) return
    win.document.open()
    win.document.write(`<!DOCTYPE html><html><head><title>${displayTitle}</title>
      <style>
        body{font-family:system-ui,sans-serif;padding:24px;color:#1c1917;max-width:100%}
        h1{font-size:1.5rem;margin-bottom:8px}.meta{font-size:.875rem;color:#78716c;margin-bottom:16px}
        .photo-gallery{display:grid;grid-template-columns:repeat(8,minmax(0,1fr));gap:6px;margin-bottom:20px}
        .photo-gallery .photo-wrap{aspect-ratio:1;overflow:hidden;border-radius:6px;border:1px solid #e7e5e4}
        .photo-gallery .photo-wrap img{width:100%;height:100%;object-fit:cover;display:block}
        .checklist{margin:16px 0;columns:3;column-gap:24px}.checklist>strong{display:block;margin-bottom:8px;column-span:all}
        .category{font-weight:600;margin:10px 0 4px;break-inside:avoid}
        .task{margin:2px 0;padding-left:18px;font-size:.8rem;break-inside:avoid}
        .task-done::before{content:"✓ ";color:#22c55e}.task-pending::before{content:"○ ";color:#78716c}
        .narrative{margin-top:20px;padding:12px 16px;background:#f5f5f4;border-radius:8px;font-style:italic;font-size:.9rem}
      </style></head><body>
      <h1>${displayTitle}</h1>
      <div class="meta">${displayReport.property?.name || ""} · ${activityDate ? new Date(activityDate).toLocaleDateString(undefined, { dateStyle: "long" }) : ""} · ${displayReport.service_type || ""} · ${operatorName || "—"}</div>
      ${displayReport.checklist_notes ? `<div class="narrative">${displayReport.checklist_notes}</div>` : ""}
      ${photosHtml}${checklistHtml}</body></html>`)
    win.document.close()
    win.focus()
    setTimeout(() => { win.print(); win.close() }, 400)
  }

  const handleDeleteClick = async () => {
    if (!displayReport.id || !onDeleteReport) return
    if (!deleteConfirming) {
      setDeleteConfirming(true)
      return
    }
    // Second tap — confirmed
    setDeleting(true)
    setDeleteConfirming(false)
    try {
      await onDeleteReport(displayReport.id)
      onClose()
    } catch (err) {
      console.error(err)
      toast("Failed to delete report", "error")
    } finally {
      setDeleting(false)
    }
  }

  // ── Edit mode ──────────────────────────────────────────────────────────────
  const editView = isEditing && displayReport.id ? (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
      <div className={`shrink-0 flex items-center justify-between px-5 py-4 border-b ${glass.borderLight}`}>
        <h3 className="text-base font-mono font-bold text-white/90 uppercase tracking-widest">Edit Activity</h3>
        <button
          type="button"
          onClick={() => setIsEditing(false)}
          className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
          aria-label="Cancel edit"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-5">
        <ReportEditForm
          reportId={displayReport.id}
          onSave={handleSaveFromEdit}
          onCancel={() => setIsEditing(false)}
          variant="embedded"
        />
      </div>
    </div>
  ) : null

  // ── Footer actions ─────────────────────────────────────────────────────────
  const footerActions = (
    <div className={`shrink-0 flex items-center justify-end gap-2 px-4 sm:px-5 py-3 border-t ${glass.borderLight} bg-stone-950/80 flex-wrap`}>
      {onEdit && displayReport.id && !isEditing && (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="py-2 px-3 rounded-lg border border-teal-400/25 bg-teal-500/20 text-teal-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-teal-500/30 transition-colors inline-flex items-center gap-1.5 touch-manipulation"
        >
          <Edit3 className="w-4 h-4" />
          Edit
        </button>
      )}

      {/* Share */}
      <div className="relative">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShareOpen((v) => !v) }}
          className={`py-2 px-3 rounded-lg border ${glass.border} bg-white/[0.04] text-white/50 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.08] transition-colors inline-flex items-center gap-1.5 touch-manipulation`}
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
        {shareOpen && (
          <div
            className={`absolute bottom-full right-0 mb-2 w-56 rounded-2xl bg-stone-950/95 backdrop-blur-2xl border ${glass.border} shadow-xl overflow-hidden z-[120]`}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" onClick={handleShare} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-white/80 hover:bg-white/[0.06] transition-colors border-b ${glass.borderLight} text-[11px] font-mono`}>
              <Share2 className="w-4 h-4 text-white/40" />
              {typeof navigator.share === "function" ? "Share…" : "Copy link"}
            </button>
            <button type="button" onClick={handleCopy} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-white/80 hover:bg-white/[0.06] transition-colors border-b ${glass.borderLight} text-[11px] font-mono`}>
              <Copy className="w-4 h-4 text-white/40" />
              {copyFeedback ? "Copied!" : "Copy summary & link"}
            </button>
            <button type="button" onClick={handlePrint} className="w-full flex items-center gap-3 px-4 py-3 text-left text-white/80 hover:bg-white/[0.06] transition-colors text-[11px] font-mono">
              <Printer className="w-4 h-4 text-white/40" />
              Print / Save as PDF
            </button>
          </div>
        )}
      </div>

      {/* Delete — two-tap inline confirmation */}
      {isAdmin && displayReport.id && onDeleteReport && (
        <div className="flex items-center gap-1.5">
          {deleteConfirming && (
            <button
              type="button"
              onClick={() => setDeleteConfirming(false)}
              className={`py-2 px-3 rounded-lg border ${glass.border} bg-white/[0.04] text-white/50 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.08] transition-colors touch-manipulation`}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={deleting}
            className={`py-2 px-3 rounded-lg border text-[10px] font-mono font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors touch-manipulation ${
              deleteConfirming
                ? "border-red-500 bg-red-500/80 text-white hover:bg-red-500 animate-pulse"
                : "border-red-400/25 bg-red-500/20 text-red-400 hover:bg-red-500/30"
            }`}
            aria-label={deleteConfirming ? "Tap again to confirm deletion" : "Delete report"}
          >
            {deleting ? (
              <span className="text-[10px]">Deleting…</span>
            ) : (
              <>
                {deleteConfirming ? <AlertTriangle className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                {deleteConfirming ? "Confirm delete" : "Delete"}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )

  // ── Modal body content ─────────────────────────────────────────────────────
  const reportMeta = (
    <div className="space-y-4 p-5 sm:p-6">
      {/* Title + status */}
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {displayReport.status && (
            <span className={`inline-block px-2.5 py-0.5 rounded-lg text-[10px] font-mono font-bold uppercase tracking-widest border ${getReportStatusGlassClasses(displayReport.status, glass.border)}`}>
              {displayReport.status}
            </span>
          )}
          {displayReport.id && (
            <span className="text-[10px] font-mono text-white/40">
              {displayReport.id.slice(0, 8).toUpperCase()}
            </span>
          )}
        </div>
        <div className="space-y-1.5 text-[11px] font-mono text-white/80">
          {displayReport.property?.name && (
            <div className="flex items-center gap-2 text-white/80">
              <MapPin className="w-3.5 h-3.5 text-white/40 shrink-0" />
              {displayReport.property.name}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/80">
            {activityDate && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-white/40 shrink-0" />
                {new Date(activityDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </span>
            )}
            {displayReport.service_type && (
              <ServiceTypePill
                serviceType={displayReport.service_type}
                className={`bg-white/[0.06] border ${glass.border} text-white/80 font-mono text-[10px]`}
              />
            )}
            <span className="flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-white/40 shrink-0" />
              {operatorName || "—"}
            </span>
            {displayReport.duration_minutes != null && (
              <span className="flex items-center gap-1.5 text-teal-400/80">
                <Clock className="w-3.5 h-3.5 shrink-0" />
                {Math.floor(displayReport.duration_minutes / 60) > 0
                  ? `${Math.floor(displayReport.duration_minutes / 60)}h ${displayReport.duration_minutes % 60}m`
                  : `${displayReport.duration_minutes}m`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      {displayReport.checklist_notes && (
        <div>
          <p className="text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5">Notes</p>
          <div className={`p-3 rounded-xl bg-white/[0.04] border ${glass.border}`}>
            {displayReport.checklist_notes.includes("<") ? (
              <RichTextRenderer html={displayReport.checklist_notes} />
            ) : (
              <p className="text-white/60 text-[13px] font-mono leading-relaxed italic">
                &ldquo;{displayReport.checklist_notes}&rdquo;
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const checklistSection = (
    <div className="px-5 sm:px-6 pt-4 pb-6 space-y-2">
      <p className="text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest flex items-center gap-2">
        <CheckCircle className="w-4 h-4 text-white/40" /> Protocol checklist
      </p>
      <ServiceChecklist checklist={checklistData} glass={glass} />
    </div>
  )

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4"
        onClick={() => shareOpen ? setShareOpen(false) : onClose()}
      >
        <div className={`absolute inset-0 ${glass.modalBg} backdrop-blur-sm`} aria-hidden />

        {/* Modal panel */}
        <div
          className={`relative w-full sm:max-w-2xl bg-stone-950/95 backdrop-blur-2xl sm:rounded-3xl rounded-t-3xl shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] border ${glass.border} overflow-hidden flex flex-col max-h-[95dvh]`}
          onClick={(e) => e.stopPropagation()}
        >
          {isEditing ? (
            editView
          ) : (
            <>
              {/* ── Sticky header ── */}
              <div className={`shrink-0 flex items-center justify-between gap-3 px-5 py-3.5 border-b ${glass.borderLight}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">0x02 · Activity</p>
                  <p className="text-base font-mono font-bold text-white/90 uppercase tracking-widest truncate pr-2">
                    {displayTitle}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors touch-manipulation"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* ── Scrollable body — stacked layout at all breakpoints ── */}
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">

                {/* Top — photo + meta */}
                <div className="flex flex-col shrink-0">
                  {/* Photo viewer — full width hero */}
                  <PhotoViewer
                    photos={photos}
                    current={currentPhoto}
                    onChange={setCurrentPhoto}
                    onExpand={photos.length > 0 ? () => setGalleryOpen(true) : undefined}
                    glass={glass}
                    className="aspect-video w-full shrink-0"
                  />
                  {/* Meta */}
                  {reportMeta}
                </div>

                {/* Bottom — intelligence (if any) + checklist */}
                <div className={`flex flex-col bg-white/[0.02] border-t ${glass.borderLight}`}>
                  {showIntelligence && (
                    <div className="px-5 sm:px-6 pt-4 pb-2">
                      <IntelligenceSection
                        report={displayReport}
                        glass={glass}
                        onPhotoClick={() => { setCurrentPhoto(0); setGalleryOpen(true) }}
                      />
                    </div>
                  )}
                  {checklistSection}
                </div>
              </div>

              {/* ── Sticky footer ── */}
              {footerActions}
            </>
          )}
        </div>
      </div>

      {/* Full-screen gallery */}
      {galleryOpen && photos.length > 0 && (
        <GalleryOverlay
          photos={photos}
          current={currentPhoto}
          onChange={setCurrentPhoto}
          onClose={() => setGalleryOpen(false)}
          glass={glass}
        />
      )}
    </>,
    document.body
  )
}
